import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { FileClock, AlertTriangle, UserCheck, Inbox } from 'lucide-react';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';

const REQUIRED_DOC_KEYS = [
  'driversLicense',
  'vehicleInsurance',
  'vehicleRegistration',
  'vehiclePhoto',
];

const DOC_LABELS: Record<string, string> = {
  driversLicense: "Driver's License",
  vehicleInsurance: 'Vehicle Insurance',
  vehicleRegistration: 'Vehicle Registration',
  vehiclePhoto: 'Vehicle Photo',
};

interface DocEntry {
  status?: string;
  expiresAt?: string | null;
  url?: string;
}

interface DriverRow {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  driverVerified?: boolean;
  verificationStatus?: string;
  driverDocuments?: Record<string, DocEntry>;
}

type ItemType = 'pending_docs' | 'expired_docs' | 'ready_to_verify';

interface InboxItem {
  id: string;
  type: ItemType;
  driver: DriverRow;
  detail: string;
}

const TYPE_META: Record<
  ItemType,
  { label: string; cls: string; Icon: typeof FileClock }
> = {
  pending_docs: {
    label: 'Docs pending review',
    cls: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    Icon: FileClock,
  },
  expired_docs: {
    label: 'Documents expired',
    cls: 'bg-red-50 text-red-700 ring-red-600/20',
    Icon: AlertTriangle,
  },
  ready_to_verify: {
    label: 'Ready to verify',
    cls: 'bg-green-50 text-green-700 ring-green-600/20',
    Icon: UserCheck,
  },
};

export default function InboxPage() {
  const [drivers, setDrivers] = useState<DriverRow[]>([]);

  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      where('roles', 'array-contains', 'driver'),
      limit(500)
    );
    return onSnapshot(q, (snap) => {
      setDrivers(snap.docs.map((d) => ({ ...(d.data() as DriverRow), id: d.id })));
    });
  }, []);

  const items = useMemo<InboxItem[]>(() => {
    const out: InboxItem[] = [];
    for (const d of drivers) {
      const docs = d.driverDocuments ?? {};
      const pending = REQUIRED_DOC_KEYS.filter(
        (k) => docs[k]?.status === 'pending_review'
      );
      const expired = REQUIRED_DOC_KEYS.filter((k) => {
        const e = docs[k];
        return (
          e?.status === 'approved' &&
          e.expiresAt != null &&
          new Date(e.expiresAt).getTime() < Date.now()
        );
      });
      const allApproved = REQUIRED_DOC_KEYS.every(
        (k) => docs[k]?.status === 'approved'
      );

      if (pending.length > 0) {
        out.push({
          id: `${d.id}-pending`,
          type: 'pending_docs',
          driver: d,
          detail: `${pending.length} awaiting review: ${pending
            .map((k) => DOC_LABELS[k])
            .join(', ')}`,
        });
      }
      if (expired.length > 0) {
        out.push({
          id: `${d.id}-expired`,
          type: 'expired_docs',
          driver: d,
          detail: `Expired: ${expired.map((k) => DOC_LABELS[k]).join(', ')}`,
        });
      }
      if (
        allApproved &&
        expired.length === 0 &&
        !d.driverVerified &&
        d.verificationStatus !== 'rejected'
      ) {
        out.push({
          id: `${d.id}-verify`,
          type: 'ready_to_verify',
          driver: d,
          detail: 'All documents approved — ready to verify this driver.',
        });
      }
    }
    // Expired first, then pending, then ready-to-verify.
    const order: ItemType[] = ['expired_docs', 'pending_docs', 'ready_to_verify'];
    return out.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
  }, [drivers]);

  const driverName = (d: DriverRow) =>
    [d.firstName, d.lastName].filter(Boolean).join(' ') || d.email || d.id;

  return (
    <>
      <PageHeader
        title="Inbox"
        subtitle="Actions that need your attention across the platform."
      />

      <Card>
        {items.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center text-slate-500">
            <Inbox size={40} className="mb-3 text-slate-300" />
            <div className="font-medium text-slate-600">You're all caught up</div>
            <div className="text-sm">
              No pending documents, expirations, or approvals right now.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item) => {
              const meta = TYPE_META[item.type];
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`mt-0.5 rounded-md p-1.5 ${meta.cls}`}>
                      <meta.Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">
                          {driverName(item.driver)}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                      <div className="truncate text-sm text-slate-500">
                        {item.detail}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/drivers?q=${encodeURIComponent(
                      item.driver.email ?? item.driver.id
                    )}`}
                    className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    Review
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}
